import * as React from 'react';
import { Bar } from 'react-chartjs-2';
import { ChartOptions } from 'chart.js';
import { ActionCount } from '../../types/analyticTypes';
import { utils_instance } from '../../utils/Utils';
import s from './analytic.module.css';
import TablePlaceholder from '../placeholders/TablePlaceholder';
import BuildingProcessingError from '../placeholders/BuildingProcessingError';

export type BreakdownGroup = {
  label: string;
  action_counts: ActionCount[];
};

type ActionBreakdownChartProps = {
  title: string;
  emptyMessage?: string;
  groups: BreakdownGroup[];
  isLoading: boolean;
  apiErrorFlag: boolean;
};

const GROUP_BLOCK_SIZE = 5;

const CHART_OPTIONS: ChartOptions<'bar'> = {
  aspectRatio: 4,
  responsive: true,
  scales: {
    x: {
      ticks: {
        autoSkip: false,
        maxRotation: 45,
        minRotation: 0,
      },
    },
    y: {
      beginAtZero: true,
      ticks: {
        precision: 0,
      },
    },
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      mode: 'index',
      intersect: false,
    },
  },
};

const deriveActionsOrder = (groups: BreakdownGroup[]): string[] => {
  const set = new Set<string>();
  groups.forEach((group) => group.action_counts.forEach((c) => set.add(c.action)));
  return Array.from(set).sort();
};

const chunkGroups = (groups: BreakdownGroup[], size: number): BreakdownGroup[][] => {
  const chunks: BreakdownGroup[][] = [];
  for (let i = 0; i < groups.length; i += size) {
    chunks.push(groups.slice(i, i + size));
  }
  return chunks;
};

const padBlockForRendering = (block: BreakdownGroup[], size: number): BreakdownGroup[] => {
  if (block.length >= size) return block;
  const placeholders: BreakdownGroup[] = Array.from({ length: size - block.length }, () => ({
    label: '',
    action_counts: [],
  }));
  return [...block, ...placeholders];
};

const ActionBreakdownChart: React.FC<ActionBreakdownChartProps> = ({
  title,
  emptyMessage,
  groups,
  isLoading,
  apiErrorFlag,
}) => {
  const actionsOrder = React.useMemo(() => deriveActionsOrder(groups), [groups]);
  const groupBlocks = React.useMemo(() => chunkGroups(groups, GROUP_BLOCK_SIZE), [groups]);

  const buildDatasetsForBlock = React.useCallback(
    (block: BreakdownGroup[]) =>
      actionsOrder.map((action, index) => ({
        label: action,
        backgroundColor: utils_instance.colorList[index % utils_instance.colorList.length],
        data: block.map(
          (group) => group.action_counts.find((c) => c.action === action)?.action_count ?? 0
        ),
      })),
    [actionsOrder]
  );

  const totalsPerAction = React.useMemo(
    () =>
      actionsOrder.map((action) =>
        groups.reduce(
          (sum, group) =>
            sum + (group.action_counts.find((c) => c.action === action)?.action_count ?? 0),
          0
        )
      ),
    [actionsOrder, groups]
  );

  const totalCount = React.useMemo(
    () => totalsPerAction.reduce((sum, value) => sum + value, 0),
    [totalsPerAction]
  );

  const legend = React.useMemo(
    () =>
      actionsOrder.map((action, index) => {
        const backgroundColor = utils_instance.colorList[index % utils_instance.colorList.length];
        const total = totalsPerAction[index];

        return (
          total !== 0 && (
            <div key={action} className={s.legend_line}>
              <div style={{ backgroundColor }} className={s.legend_color} />
              <p className={s.legend_text}>
                {action} - {total}
              </p>
            </div>
          )
        );
      }),
    [actionsOrder, totalsPerAction]
  );

  const hasGroups = groups.length > 0;
  const hasAnyAction = actionsOrder.length > 0;

  return (
    <div className={`${s.content} ${s.paper_style}`}>
      <div>
        <div className={s.fields}>
          <div className={s.card_header}>{title}</div>
          {isLoading ? (
            <TablePlaceholder rows={9} columns={['analytics']} showHeader={false} />
          ) : (
            <div>
              {apiErrorFlag ? (
                <BuildingProcessingError entity={'analytics'} />
              ) : hasGroups && hasAnyAction ? (
                <div className={s.breakdown_grid}>
                  {groupBlocks.map((block, blockIndex) => {
                    const from = blockIndex * GROUP_BLOCK_SIZE + 1;
                    const to = from + block.length - 1;
                    const renderedBlock = padBlockForRendering(block, GROUP_BLOCK_SIZE);
                    return (
                      <div key={blockIndex}>
                        {groupBlocks.length > 1 && (
                          <p className={s.breakdown_block_label}>
                            {from === to ? `#${from}` : `#${from}–${to}`}
                          </p>
                        )}
                        <Bar
                          options={CHART_OPTIONS}
                          data={{
                            labels: renderedBlock.map((group) => group.label),
                            datasets: buildDatasetsForBlock(renderedBlock),
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p>{emptyMessage ?? 'No data for the selected filters.'}</p>
              )}
            </div>
          )}
        </div>
        {!isLoading && !apiErrorFlag && hasGroups && hasAnyAction && (
          <div>
            <p>Actions in this period:</p>
            <div className={s.legend_container}>{legend}</div>
            <p>Total action amount - {totalCount}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActionBreakdownChart;
